/**
 * What the director reads from, and does to, the canvas page: boxes on
 * screen, the canvas's own selection event, and a marquee drawn with React
 * Flow's own element so it looks like the product's.
 */

import { COVERABLE } from "./chrome";
import type { Box, Geometry } from "./layout";
import { Cancelled, sleep, type Signal } from "./needs";

export const PANE_SELECTOR = ".react-flow";
export const CHAT_WINDOW_SELECTOR = '[data-floating-chat="window"]';
export const RIGHT_PANE_SELECTOR = ".js-right-pane-panel";
const TOOLBAR_SELECTOR = '[aria-label="Canvas tools"]';
const NODE_SELECTOR = ".react-flow__node[data-id]";
const MARQUEE_HOST_SELECTOR = ".react-flow__pane";

export function targetSelector(target: string): string {
  return `[data-onboarding-target="${target}"]`;
}

/** Select exactly these nodes, through the canvas's own window event. */
export function selectNodes(nodeIds: readonly string[]): void {
  window.dispatchEvent(
    new CustomEvent("canvas:select-nodes", { detail: { nodeIds } }),
  );
}

export function boxOf(el: Element | null): Box | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { left: r.left, top: r.top, width: r.width, height: r.height };
}

const find = (selector: string) => boxOf(document.querySelector(selector));

/** The right pane keeps a sliver of itself in the page while it is closed. */
const PANE_OPEN_PX = 40;
function rightPaneBox(): Box | null {
  const box = find(RIGHT_PANE_SELECTOR);
  return box && box.width >= PANE_OPEN_PX ? box : null;
}

/** The box around everything a selector matches. */
function around(selector: string): Box | null {
  let all: Box | null = null;
  for (const el of document.querySelectorAll(selector)) {
    const box = boxOf(el);
    if (!box) continue;
    if (!all) {
      all = box;
      continue;
    }
    const left = Math.min(all.left, box.left);
    const top = Math.min(all.top, box.top);
    all = {
      left,
      top,
      width: Math.max(all.left + all.width, box.left + box.width) - left,
      height: Math.max(all.top + all.height, box.top + box.height) - top,
    };
  }
  return all;
}

export interface ScreenWants {
  chat: "closed" | "tour";
  rightPane: boolean;
  target: string | null;
}

export function measure(wants: ScreenWants): Geometry | null {
  const pane = find(PANE_SELECTOR);
  if (!pane) return null;
  const chat = wants.chat === "tour" ? find(CHAT_WINDOW_SELECTOR) : null;
  return {
    viewport: { width: window.innerWidth, height: window.innerHeight },
    pane,
    chat,
    rightPane: wants.rightPane ? rightPaneBox() : null,
    target: wants.target ? find(targetSelector(wants.target)) : null,
    toolbar: find(TOOLBAR_SELECTOR),
    controls: around(COVERABLE.controls),
  };
}

/** How long the screen must hold still after a chat or a pane was moved. */
export const HOLD_MS = 100;
/** The same when the scene asked for nothing to move. */
export const QUIET_HOLD_MS = 50;
const SAMPLE_MS = 25;
const SETTLE_CAP_MS = 2500;
/** The screen as the last settle left it. */
let lastSettled = "";

/**
 * Resolves once the screen is what the scene asked for and has held still:
 * the pane, the chat window and the right pane stop moving after a rail, a
 * chat or a pane opens or closes. One wait per scene set, not a watch.
 */
export async function settled(
  wants: ScreenWants,
  signal: Signal,
  holdMs = HOLD_MS,
): Promise<void> {
  let last = "";
  // By the clock, not by the count: a sample that comes late behind the
  // product's own rendering has still seen the screen hold.
  let since = performance.now();
  for (let waited = 0; waited < SETTLE_CAP_MS; waited += SAMPLE_MS) {
    if (signal.cancelled) throw new Cancelled();
    const chat = find(CHAT_WINDOW_SELECTOR);
    const rightPane = rightPaneBox();
    const target = wants.target ? find(targetSelector(wants.target)) : null;
    const present =
      (wants.chat === "tour") === !!chat &&
      wants.rightPane === !!rightPane &&
      (!wants.target || !!target);
    const key = JSON.stringify([
      window.innerWidth,
      window.innerHeight,
      find(PANE_SELECTOR),
      chat,
      rightPane,
      target,
    ]);
    const now = performance.now();
    if (!present || key !== last) since = now;
    last = key;
    // A quiet step whose screen is as the last settle left it has nothing
    // to wait for, and every wait here can queue behind a product render.
    const unchanged = holdMs < HOLD_MS && present && key === lastSettled;
    if (unchanged || now - since >= holdMs) {
      lastSettled = key;
      return;
    }
    await sleep(SAMPLE_MS);
  }
}

export function frames(count: number): Promise<void> {
  return new Promise((resolve) => {
    const step = (left: number) =>
      left <= 0 ? resolve() : requestAnimationFrame(() => step(left - 1));
    step(count);
  });
}

const overlaps = (a: Box, b: Box) =>
  Math.min(a.left + a.width, b.left + b.width) > Math.max(a.left, b.left) &&
  Math.min(a.top + a.height, b.top + b.height) > Math.max(a.top, b.top);

function nodeBoxes(ids: readonly string[]): { id: string; box: Box }[] {
  const wanted = new Set(ids);
  const found: { id: string; box: Box }[] = [];
  for (const el of document.querySelectorAll<HTMLElement>(NODE_SELECTOR)) {
    const id = el.dataset.id;
    const box = boxOf(el);
    if (id && box && wanted.has(id)) found.push({ id, box });
  }
  return found;
}

/** The one of `ids` under a point on screen, if any. */
export function nodeAt(
  x: number,
  y: number,
  ids: readonly string[],
): string | null {
  const point = { left: x, top: y, width: 1, height: 1 };
  return nodeBoxes(ids).find(({ box }) => overlaps(box, point))?.id ?? null;
}

/** Those of `ids` a box on screen touches, as the canvas's marquee picks. */
export function nodesIn(area: Box, ids: readonly string[]): string[] {
  return nodeBoxes(ids)
    .filter(({ box }) => overlaps(box, area))
    .map(({ id }) => id);
}

export const boxBetween = (
  a: { x: number; y: number },
  b: { x: number; y: number },
): Box => ({
  left: Math.min(a.x, b.x),
  top: Math.min(a.y, b.y),
  width: Math.abs(a.x - b.x),
  height: Math.abs(a.y - b.y),
});

export interface Marquee {
  draw: (area: Box) => void;
  remove: () => void;
}

/**
 * React Flow's selection rectangle, with its classes, where React Flow puts
 * it: whatever the canvas styles its marquee with styles this one.
 */
export function createMarquee(): Marquee {
  const host = document.querySelector<HTMLElement>(MARQUEE_HOST_SELECTOR);
  const el = document.createElement("div");
  el.className = "react-flow__selection react-flow__container";
  el.style.pointerEvents = "none";
  host?.appendChild(el);
  return {
    draw(area) {
      const origin = host?.getBoundingClientRect();
      el.style.width = `${area.width}px`;
      el.style.height = `${area.height}px`;
      el.style.transform = `translate(${area.left - (origin?.left ?? 0)}px, ${
        area.top - (origin?.top ?? 0)
      }px)`;
    },
    remove() {
      el.remove();
    },
  };
}
